import { AsyncPipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ProvisionUploadReportsAction } from '@app/modules/reports/store/reports.actions';
import { ReportsState } from '@app/modules/reports/store/reports.state';
import { Logger } from '@core/services/logger.service';
import { SigneeState } from '@core/store/signees/signees.state';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  TranslateDirective,
  TranslatePipe,
  TranslateService,
} from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ConfirmService } from '@shared/components/confirm/confirm-dialog.component';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  DataTableActionsLocation,
  DataTableActionType,
  DataTableConfiguration,
  DataTableSelectionType,
} from '@shared/components/data-table/data-table.types';
import { SelectOptionComponent } from '@shared/components/select/select-option/select-option.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { ConfirmCloseReason } from '@shared/shared.types';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-upload-reports-form',
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    SelectComponent,
    SelectOptionComponent,
    TranslatePipe,
    TranslateDirective,
    DataTableComponent,
  ],
  templateUrl: './upload-reports-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './upload-reports-form.component.scss',
})
export class UploadReportsFormComponent implements OnInit {
  private modal = inject(NgbActiveModal);
  private confirmService = inject(ConfirmService);
  private translateService = inject(TranslateService);
  private store = inject(Store);
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);

  dataTableConfiguration: DataTableConfiguration;
  rows$ = new BehaviorSubject<File[]>([]);

  filesToProcess: File[];
  files: File[];

  private readonly log = new Logger('UploadResultsDialogComponent');
  protected signees$ = this.store.select(SigneeState.getSigneeList);
  protected uploadResult$ = this.store.select(ReportsState.getUploadResult);

  protected form = this.fb.group({
    signeeId: [null, Validators.required],
  });

  ngOnInit() {
    this.dataTableConfiguration = {
      title: '',
      selectionMode: DataTableSelectionType.NONE,
      tableHeadClasses: '',
      actionsLocation: DataTableActionsLocation.RIGHT,
      singleActions: [
        {
          type: DataTableActionType.BUTTON,
          name: 'Remove file',
          icon: 'fa-light fa-xmark',
          action: this.removeFile.bind(this),
        },
      ],
      columns: [
        {
          label: 'File Name',
          property: 'name',
        },
      ],
      rows$: this.rows$,
    };
  }

  onFileChange(event: any) {
    this.files = Array.from(event.target.files);
    this.filesToProcess =
      this.filesToProcess != null ? Array.from(this.filesToProcess) : null;

    if (this.filesToProcess != null && this.filesToProcess.length !== 0) {
      for (const file of this.files) {
        if (this.filesToProcess.findIndex((f) => f.name === file.name) !== -1) {
          continue;
        }
        this.filesToProcess.push(file);
      }
    } else {
      this.filesToProcess = this.files;
    }
    this.rows$.next(this.filesToProcess);
  }

  upload(event: MouseEvent) {
    this.log.debug('upload');
    event.preventDefault();

    this.store
      .dispatch(
        new ProvisionUploadReportsAction(
          this.filesToProcess,
          this.form.get('signeeId').value
        )
      )
      .subscribe(() => {
        this.modal.close();
      });
  }

  removeFile(event: MouseEvent, row: File) {
    event.preventDefault();

    this.confirmService
      .confirm({
        message: this.translateService.instant(
          'pii.list.uploadDocuments.confirmFileRemove.message',
          { fileName: row.name }
        ),
        title: 'pii.list.uploadDocuments.confirmFileRemove.title',
      })
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe((confirmOpt) => {
        if (confirmOpt.closeReason === ConfirmCloseReason.Yes) {
          const index = this.filesToProcess.findIndex(
            (f) => f.name === row.name
          );
          this.filesToProcess.splice(index, 1);
          this.rows$.next(this.filesToProcess);
        }
      });
  }

  dismiss() {
    this.modal.dismiss();
  }
}
